import {
    parseDate,
    parseFixedLength,
    parseFixedLengthMeasure,
    parseVariableLength,
    parseVariableLengthMeasure,
    parseVariableLengthWithISOChars,
    parseVariableLengthWithISONumbers,
} from './scan-parser.utils';
import { ScanResult } from './scan.interface';

export const fourthNum = (codeString: string): string => codeString.slice(3, 4);

const scanHandlers: Record<string, (codeString: string) => ScanResult> = {
    // SSCC (Serial Shipping Container Code)
    '00': (codeString) => parseFixedLength('00', 'SSCC', 18, codeString),
    // Global Trade Item Number (GTIN)
    '01': (codeString) => parseFixedLength('01', 'GTIN', 14, codeString),
    // GTIN of Contained Trade Items
    '02': (codeString) => parseFixedLength('02', 'CONTENT', 14, codeString),

    // Batch or Lot Number
    '10': (codeString) => parseVariableLength('10', 'BATCH/LOT', codeString),
    // Production Date (YYMMDD)
    '11': (codeString) => parseDate('11', 'PROD DATE', codeString),
    // Due Date (YYMMDD)
    '12': (codeString) => parseDate('12', 'DUE DATE', codeString),
    // Packaging Date (YYMMDD)
    '13': (codeString) => parseDate('13', 'PACK DATE', codeString),
    // AI "14" isn't defined
    // Best Before Date (YYMMDD)
    '15': (codeString) => parseDate('15', 'BEST BEFORE or BEST BY', codeString),
    // Sell By Date (YYMMDD)
    '16': (codeString) => parseDate('16', 'SELL BY', codeString),
    // Expiration Date (YYMMDD)
    '17': (codeString) => parseDate('17', 'USE BY OR EXPIRY', codeString),

    // Variant Number
    '20': (codeString) => parseFixedLength('20', 'VARIANT', 2, codeString),
    // Serial Number
    '21': (codeString) => parseVariableLength('21', 'SERIAL', codeString),
    // AI 22, 23 are not defined

    // Additional Item Identification
    '240': (codeString) => parseVariableLength('240', 'ADDITIONAL ID', codeString),
    // Customer Part Number
    '241': (codeString) => parseVariableLength('241', 'CUST. PART NO.', codeString),
    // Made-to-Order Variation Number
    '242': (codeString) => parseVariableLength('242', 'MTO VARIANT', codeString),
    // Packaging Component Number
    '243': (codeString) => parseVariableLength('243', 'PCN', codeString),
    // Secondary Serial Number
    '250': (codeString) => parseVariableLength('250', 'SECONDARY SERIAL', codeString),
    // Reference to Source Entity
    '251': (codeString) => parseVariableLength('251', 'REF. TO SOURCE', codeString),
    // AI "252" isn't defined
    // Global Document Type Identifier (GDTI)
    '253': (codeString) => parseVariableLength('253', 'GDTI', codeString),
    // GLN Extension Component
    '254': (codeString) => parseVariableLength('254', 'GLN EXTENSION COMPONENT', codeString),
    // Global Coupon Number (GCN)
    '255': (codeString) => parseVariableLength('255', 'GCN', codeString),

    // AI "26" to "29" aren't defined

    // Count of Items (Variable Measure Trade Item)
    '30': (codeString) => parseVariableLength('30', 'VARCOUNT', codeString),

    // Net weight, kilograms (Variable Measure Trade Item)
    '310': (codeString) => parseFixedLengthMeasure('310', fourthNum(codeString), 'NET WEIGHT (kg)', 'KGM', codeString),
    // Length or first dimension, metres (Variable Measure Trade Item)
    '311': (codeString) => parseFixedLengthMeasure('311', fourthNum(codeString), 'LENGTH (m)', 'MTR', codeString),
    // Width, diameter, or second dimension, metres (Variable Measure Trade Item)
    '312': (codeString) => parseFixedLengthMeasure('312', fourthNum(codeString), 'WIDTH (m)', 'MTR', codeString),
    // Depth, thickness, height, or third dimension, metres (Variable Measure Trade Item)
    '313': (codeString) => parseFixedLengthMeasure('313', fourthNum(codeString), 'HEIGHT (m)', 'MTR', codeString),
    // Area, square metres (Variable Measure Trade Item)
    '314': (codeString) => parseFixedLengthMeasure('314', fourthNum(codeString), 'AREA (m2)', 'MTK', codeString),
    // Net volume, litres (Variable Measure Trade Item)
    '315': (codeString) => parseFixedLengthMeasure('315', fourthNum(codeString), 'NET VOLUME (l)', 'LTR', codeString),
    // Net volume, cubic metres (Variable Measure Trade Item)
    '316': (codeString) => parseFixedLengthMeasure('316', fourthNum(codeString), 'NET VOLUME (m3)', 'MTQ', codeString),

    // Net weight, pounds (Variable Measure Trade Item)
    '320': (codeString) => parseFixedLengthMeasure('320', fourthNum(codeString), 'NET WEIGHT (lb)', 'LBR', codeString),
    // Length or first dimension, inches (Variable Measure Trade Item)
    '321': (codeString) => parseFixedLengthMeasure('321', fourthNum(codeString), 'LENGTH (i)', 'INH', codeString),
    // Length or first dimension, feet (Variable Measure Trade Item)
    '322': (codeString) => parseFixedLengthMeasure('322', fourthNum(codeString), 'LENGTH (f)', 'FOT', codeString),
    // Length or first dimension, yards (Variable Measure Trade Item)
    '323': (codeString) => parseFixedLengthMeasure('323', fourthNum(codeString), 'LENGTH (y)', 'YRD', codeString),
    // Width, diameter, or second dimension, inches (Variable Measure Trade Item)
    '324': (codeString) => parseFixedLengthMeasure('324', fourthNum(codeString), 'WIDTH (i)', 'INH', codeString),
    // Width, diameter, or second dimension, feet (Variable Measure Trade Item)
    '325': (codeString) => parseFixedLengthMeasure('325', fourthNum(codeString), 'WIDTH (f)', 'FOT', codeString),
    // Width, diameter, or second dimension, yards (Variable Measure Trade Item
    '326': (codeString) => parseFixedLengthMeasure('326', fourthNum(codeString), 'WIDTH (y)', 'YRD', codeString),
    // Depth, thickness, height, or third dimension, inches (Variable Measure Trade Item)
    '327': (codeString) => parseFixedLengthMeasure('327', fourthNum(codeString), 'HEIGHT (i)', 'INH', codeString),
    // Depth, thickness, height, or third dimension, feet (Variable Measure Trade Item)
    '328': (codeString) => parseFixedLengthMeasure('328', fourthNum(codeString), 'HEIGHT (f)', 'FOT', codeString),
    // Depth, thickness, height, or third dimension, yards (Variable Measure Trade Item)
    '329': (codeString) => parseFixedLengthMeasure('329', fourthNum(codeString), 'HEIGHT (y)', 'YRD', codeString),

    // Logistic weight, kilograms
    '330': (codeString) =>
        parseFixedLengthMeasure('330', fourthNum(codeString), 'GROSS WEIGHT (kg)', 'KGM', codeString),
    // Length or first dimension, metres
    '331': (codeString) => parseFixedLengthMeasure('331', fourthNum(codeString), 'LENGTH (m), log', 'MTR', codeString),
    // Width, diameter, or second dimension, metres
    '332': (codeString) => parseFixedLengthMeasure('332', fourthNum(codeString), 'WIDTH (m), log', 'MTR', codeString),
    // Depth, thickness, height, or third dimension, metres
    '333': (codeString) => parseFixedLengthMeasure('333', fourthNum(codeString), 'HEIGHT (m), log', 'MTR', codeString),
    // Area, square metres
    '334': (codeString) => parseFixedLengthMeasure('334', fourthNum(codeString), 'AREA (m2), log', 'MTK', codeString),
    // Logistic volume, litres
    '335': (codeString) => parseFixedLengthMeasure('335', fourthNum(codeString), 'VOLUME (l), log', 'LTR', codeString),
    // Logistic volume, cubic metres
    '336': (codeString) => parseFixedLengthMeasure('336', fourthNum(codeString), 'VOLUME (m3), log', 'MTQ', codeString),
    // Kilograms per square metre, yes, the ISO code for this _is_ "28".
    '337': (codeString) => parseFixedLengthMeasure('337', fourthNum(codeString), 'KG PER m²', '28', codeString),

    // Logistic weight, pounds
    '340': (codeString) =>
        parseFixedLengthMeasure('340', fourthNum(codeString), 'GROSS WEIGHT (lb)', 'LBR', codeString),
    // Length or first dimension, inches
    '341': (codeString) => parseFixedLengthMeasure('341', fourthNum(codeString), 'LENGTH (i), log', 'INH', codeString),
    // Length or first dimension, feet
    '342': (codeString) => parseFixedLengthMeasure('342', fourthNum(codeString), 'LENGTH (f), log', 'FOT', codeString),
    // Length or first dimension, yards
    '343': (codeString) => parseFixedLengthMeasure('343', fourthNum(codeString), 'LENGTH (y), log', 'YRD', codeString),
    // Width, diameter, or second dimension, inches
    '344': (codeString) => parseFixedLengthMeasure('344', fourthNum(codeString), 'WIDTH (i), log', 'INH', codeString),
    // Width, diameter, or second dimension, feet
    '345': (codeString) => parseFixedLengthMeasure('345', fourthNum(codeString), 'WIDTH (f), log', 'FOT', codeString),
    // Width, diameter, or second dimension, yard
    '346': (codeString) => parseFixedLengthMeasure('346', fourthNum(codeString), 'WIDTH (y), log', 'YRD', codeString),
    // Depth, thickness, height, or third dimension, inches
    '347': (codeString) => parseFixedLengthMeasure('347', fourthNum(codeString), 'HEIGHT (i), log', 'INH', codeString),
    // Depth, thickness, height, or third dimension, feet
    '348': (codeString) => parseFixedLengthMeasure('348', fourthNum(codeString), 'HEIGHT (f), log', 'FOT', codeString),
    // Depth, thickness, height, or third dimension, yards
    '349': (codeString) => parseFixedLengthMeasure('349', fourthNum(codeString), 'HEIGHT (y), log', 'YRD', codeString),

    // Area, square inches (Variable Measure Trade Item)
    '350': (codeString) => parseFixedLengthMeasure('350', fourthNum(codeString), 'AREA (i2)', 'INK', codeString),
    // Area, square feet (Variable Measure Trade Item)
    '351': (codeString) => parseFixedLengthMeasure('351', fourthNum(codeString), 'AREA (f2)', 'FTK', codeString),
    // Area, square yards (Variable Measure Trade Item)
    '352': (codeString) => parseFixedLengthMeasure('352', fourthNum(codeString), 'AREA (y2)', 'YDK', codeString),
    // Area, square inches
    '353': (codeString) => parseFixedLengthMeasure('353', fourthNum(codeString), 'AREA (i2), log', 'INK', codeString),
    // Area, square feet
    '354': (codeString) => parseFixedLengthMeasure('354', fourthNum(codeString), 'AREA (f2), log', 'FTK', codeString),
    // Area, square yards
    '355': (codeString) => parseFixedLengthMeasure('355', fourthNum(codeString), 'AREA (y2), log', 'YDK', codeString),
    // Net weight, troy ounces (Variable Measure Trade Item)
    '356': (codeString) => parseFixedLengthMeasure('356', fourthNum(codeString), 'NET WEIGHT (t)', 'APZ', codeString),
    // Net weight (or volume), ounces (Variable Measure Trade Item)
    '357': (codeString) => parseFixedLengthMeasure('357', fourthNum(codeString), 'NET VOLUME (oz)', 'ONZ', codeString),

    // Net volume, quarts (Variable Measure Trade Item)
    '360': (codeString) => parseFixedLengthMeasure('360', fourthNum(codeString), 'NET VOLUME (q)', 'QT', codeString),
    // Net volume, gallons U.S. (Variable Measure Trade Item)
    '361': (codeString) => parseFixedLengthMeasure('361', fourthNum(codeString), 'NET VOLUME (g)', 'GLL', codeString),
    // Logistic volume, quarts
    '362': (codeString) => parseFixedLengthMeasure('362', fourthNum(codeString), 'VOLUME (q), log', 'QT', codeString),
    // Logistic volume, gallons U.S.
    '363': (codeString) => parseFixedLengthMeasure('363', fourthNum(codeString), 'VOLUME (g), log', 'GLL', codeString),
    // Net volume, cubic inches (Variable Measure Trade Item)
    '364': (codeString) => parseFixedLengthMeasure('364', fourthNum(codeString), 'VOLUME (i3)', 'INQ', codeString),
    // Net volume, cubic feet (Variable Measure Trade Item)
    '365': (codeString) => parseFixedLengthMeasure('365', fourthNum(codeString), 'VOLUME (f3)', 'FTQ', codeString),
    // Net volume, cubic yards (Variable Measure Trade Item)
    '366': (codeString) => parseFixedLengthMeasure('366', fourthNum(codeString), 'VOLUME (y3)', 'YDQ', codeString),
    // Logistic volume, cubic inches
    '367': (codeString) => parseFixedLengthMeasure('367', fourthNum(codeString), 'VOLUME (i3), log', 'INQ', codeString),
    // Logistic volume, cubic feet
    '368': (codeString) => parseFixedLengthMeasure('368', fourthNum(codeString), 'VOLUME (f3), log', 'FTQ', codeString),
    // Logistic volume, cubic yards
    '369': (codeString) => parseFixedLengthMeasure('369', fourthNum(codeString), 'VOLUME (y3), log', 'YDQ', codeString),

    // Count of Trade Items
    '37': (codeString) => parseVariableLength('37', 'COUNT', codeString),

    // AI "38" isn't defined

    // Applicable Amount Payable, local currency
    '390': (codeString) => parseVariableLengthMeasure('390', fourthNum(codeString), 'AMOUNT', '', codeString),
    // Applicable Amount Payable with ISO Currency Code
    '391': (codeString) => parseVariableLengthWithISONumbers('391', fourthNum(codeString), 'AMOUNT', codeString),
    // Applicable Amount Payable, single monetary area (Variable Measure Trade Item)
    '392': (codeString) => parseVariableLengthMeasure('392', fourthNum(codeString), 'PRICE', '', codeString),
    // Applicable Amount Payable with ISO Currency Code (Variable Measure Trade Item)
    '393': (codeString) => parseVariableLengthWithISONumbers('393', fourthNum(codeString), 'PRICE', codeString),

    // Customer's Purchase Order Number
    '400': (codeString) => parseVariableLength('400', 'ORDER NUMBER', codeString),
    // Global Identification Number for Consignment (GINC)
    '401': (codeString) => parseVariableLength('401', 'GINC', codeString),
    // Global Shipment Identification Number (GSIN)
    // should be 17 digits long
    '402': (codeString) => parseVariableLength('402', 'GSIN', codeString),
    // Routing Code
    '403': (codeString) => parseVariableLength('403', 'ROUTE', codeString),

    // Ship to - Deliver to Global Location Number
    '410': (codeString) => parseFixedLength('410', 'SHIP TO LOC', 13, codeString),
    // Bill to - Invoice to Global Location Number
    '411': (codeString) => parseFixedLength('411', 'BILL TO', 13, codeString),
    // Purchased from Global Location Number
    '412': (codeString) => parseFixedLength('412', 'PURCHASE FROM', 13, codeString),
    // Ship for - Deliver for - Forward to Global Location Number
    '413': (codeString) => parseFixedLength('413', 'SHIP FOR LOC', 13, codeString),

    // Ship to - Deliver to Postal Code Within a Single Postal Authority
    '420': (codeString) => parseVariableLength('420', 'SHIP TO POST', codeString),
    // Ship to - Deliver to Postal Code with ISO Country Code
    '421': (codeString) => parseVariableLengthWithISOChars('421', 'SHIP TO POST', codeString),
    // Country of Origin of a Trade Item
    '422': (codeString) => parseFixedLength('422', 'ORIGIN', 3, codeString),
    // Country of Initial Processing
    // Up to 5 3-digit ISO-countrycodes
    '423': (codeString) => parseVariableLength('423', 'COUNTRY - INITIAL PROCESS.', codeString),
    // Country of Processing
    '424': (codeString) => parseFixedLength('424', 'COUNTRY - PROCESS.', 3, codeString),
    // Country of Disassembly
    '425': (codeString) => parseFixedLength('425', 'COUNTRY - DISASSEMBLY', 3, codeString),
    // Country Covering full Process Chain
    '426': (codeString) => parseFixedLength('426', 'COUNTRY – FULL PROCESS', 3, codeString),
    // Country Subdivision of Origin
    '427': (codeString) => parseVariableLength('427', 'ORIGIN SUBDIVISION', codeString),

    // first digits 5 and 6 are not used

    // NATO Stock Number (NSN)
    // should be 13 digits long
    '7001': (codeString) => parseVariableLength('7001', 'NSN', codeString),
    // UN/ECE Meat Carcasses and Cuts Classification
    '7002': (codeString) => parseVariableLength('7002', 'MEAT CUT', codeString),
    // Expiration Date and Time
    // should be 10 digits long
    '7003': (codeString) => parseVariableLength('7003', 'EXPIRY TIME', codeString),
    // Active Potency
    '7004': (codeString) => parseVariableLength('7004', 'ACTIVE POTENCY', codeString),

    // 701 and 702 are not used

    // Approval Number of Processor with ISO Country Code
    // Title and stem for parsing are build from 4th number:
    '703': (codeString) =>
        parseVariableLengthWithISOChars(
            '703' + fourthNum(codeString),
            'PROCESSOR # ' + fourthNum(codeString),
            codeString,
        ),

    // National Healthcare Reimbursement Number (NHRN) – Germany PZN
    '710': (codeString) => parseVariableLength('710', 'NHRN PZN', codeString),
    // National Healthcare Reimbursement Number (NHRN) – France CIP
    '711': (codeString) => parseVariableLength('711', 'NHRN CIP', codeString),
    // National Healthcare Reimbursement Number (NHRN) – Spain CN
    '712': (codeString) => parseVariableLength('712', 'NHRN CN', codeString),
    // National Healthcare Reimbursement Number (NHRN) – Brasil DRN
    '713': (codeString) => parseVariableLength('713', 'NHRN DRN', codeString),

    // Roll Products (Width, Length, Core Diameter, Direction, Splices)
    // should be 14 digits long
    '8001': (codeString) => parseVariableLength('8001', 'DIMENSIONS', codeString),
    // Cellular Mobile Telephone Identifier
    '8002': (codeString) => parseVariableLength('8002', 'CMT No', codeString),
    // Global Returnable Asset Identifier (GRAI)
    // should contain at least 14 digits
    '8003': (codeString) => parseVariableLength('8003', 'GRAI', codeString),
    // Global Individual Asset Identifier (GIAI)
    '8004': (codeString) => parseVariableLength('8004', 'GIAI', codeString),
    // Price Per Unit of Measure
    // should be 6 digits long
    '8005': (codeString) => parseVariableLength('8005', 'PRICE PER UNIT', codeString),
    // Identification of the Components of a Trade Item
    // should be exactly 18 digits long
    '8006': (codeString) => parseVariableLength('8006', 'GCTIN', codeString),
    // International Bank Account Number (IBAN)
    '8007': (codeString) => parseVariableLength('8007', 'IBAN', codeString),
    // Date and Time of Production
    // should be exactly 12 digits long
    '8008': (codeString) => parseVariableLength('8008', 'PROD TIME', codeString),

    // Component / Part Identifier (CPID)
    '8010': (codeString) => parseVariableLength('8010', 'CPID', codeString),
    // Component / Part Identifier Serial Number (CPID SERIAL)
    '8011': (codeString) => parseVariableLength('8011', 'CPID SERIAL', codeString),
    // Global Service Relation Number to identify the relationship between an organisation offering services and the provider of services
    // should be 18 digits long
    '8017': (codeString) => parseVariableLength('8017', 'GSRN - PROVIDER', codeString),
    // Global Service Relation Number to identify the relationship between an organisation offering services and the recipient of services
    // should be 18 digits long
    '8018': (codeString) => parseVariableLength('8018', 'GSRN - RECIPIENT', codeString),
    // Service Relation Instance Number (SRIN)
    '8019': (codeString) => parseVariableLength('8019', 'SRIN', codeString),

    // Payment Slip Reference Number
    '8020': (codeString) => parseVariableLength('8020', 'REF No', codeString),

    // GS1-128 Coupon Extended Code
    // should be 6 digits long
    '8100': (codeString) => parseVariableLength('8100', '-', codeString),
    // GS1-128 Coupon Extended Code
    // should be 10 digits long
    '8101': (codeString) => parseVariableLength('8101', '-', codeString),
    // GS1-128 Coupon Extended Code
    // should be 2 digits long
    '8102': (codeString) => parseVariableLength('8102', '-', codeString),

    // Coupon Code Identification for Use in North America
    '8110': (codeString) => parseVariableLength('8110', '-', codeString),

    // Extended Packaging URL
    '8200': (codeString) => parseVariableLength('8200', 'PRODUCT URL', codeString),

    // Information Mutually Agreed Between Trading Partners
    '90': (codeString) => parseVariableLength('90', 'INTERNAL', codeString),
    // Company Internal Information
    '91': (codeString) => parseVariableLength('91', 'INTERNAL', codeString),
    // Company Internal Information
    '92': (codeString) => parseVariableLength('92', 'INTERNAL', codeString),
    // Company Internal Information
    '93': (codeString) => parseVariableLength('93', 'INTERNAL', codeString),
    // Company Internal Information
    '94': (codeString) => parseVariableLength('94', 'INTERNAL', codeString),
    // Company Internal Information
    '95': (codeString) => parseVariableLength('95', 'INTERNAL', codeString),
    // Company Internal Information
    '96': (codeString) => parseVariableLength('96', 'INTERNAL', codeString),
    // Company Internal Information
    '97': (codeString) => parseVariableLength('97', 'INTERNAL', codeString),
    // Company Internal Information
    '98': (codeString) => parseVariableLength('98', 'INTERNAL', codeString),
    // Company Internal Information
    '99': (codeString) => parseVariableLength('99', 'INTERNAL', codeString),
};

export function scanSwitch(codeString: string): ScanResult {
    const possibleKeys = [codeString.slice(0, 4), codeString.slice(0, 3), codeString.slice(0, 2)];
    const handler = possibleKeys.find((key) => scanHandlers[key]);

    if (handler) {
        return scanHandlers[handler](codeString);
    }

    throw new Error(`scanSwitch: identifyApplicationIdentifier: Could not find match for code string ${codeString}`);
}
